import type { UTCDate } from '@date-fns/utc';

import { DEFAULT_FILTERS } from '../contexts/defaultFilters';
import { useFilterContext } from '../contexts/FilterProvider';
import { getFilterDefinition } from '../engine/filterRegistry';
import {
  type DateRangeFilter,
  type Filter,
  FilterId,
  FilterType,
} from '../types/filters';

export const useFilters = () => {
  const { activeFilterMap, dispatch } = useFilterContext();

  const removeFilter = (filterId: FilterId) => {
    dispatch({ type: 'REMOVE_FILTER', filterId });
  };

  return {
    filters: activeFilterMap,
    secondaryFilters: DEFAULT_FILTERS,
    removeFilter,
  };
};

export const useFilterState = <T extends Filter = Filter>(
  filterId: FilterId
) => {
  const { activeFilterMap, dispatch } = useFilterContext();

  const filterState =
    (activeFilterMap.get(filterId) as T) ||
    (getFilterDefinition(filterId) as T);

  const updateFilter = (updatedFilter: Partial<Filter> & { id: FilterId }) => {
    dispatch({ type: 'UPDATE_FILTER', filter: updatedFilter });
  };

  const removeTreeFilterValue = (filterValueId: string) => {
    if (filterState?.type === FilterType.TREE) {
      const newSelections = filterState.selections.filter(
        (selection) => selection.id !== filterValueId
      );

      updateFilter({ id: filterId, selections: newSelections });
    }
  };

  const removeFilter = () => {
    dispatch({ type: 'REMOVE_FILTER', filterId });
  };

  return {
    filterState,
    updateFilter,
    removeTreeFilterValue,
    removeFilter,
  };
};

export const useDateRangeFilter = () => {
  const { filterState, updateFilter } = useFilterState<DateRangeFilter>(
    FilterId.DATE_RANGE
  );

  const setDateRange = (min: UTCDate | null, max: UTCDate | null) => {
    updateFilter({
      ...filterState,
      id: FilterId.DATE_RANGE,
      min,
      max,
    });
  };

  return { dateRange: filterState, setDateRange };
};
